import React from 'react';
import { connect } from 'react-redux';
import { hashHistory } from 'react-router';
import {
  Grid, Box, Typography, Chip, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, Tooltip,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import ButtonInc from '../../shared-ui/ButtonInc';
import ModalInc from '../../shared-ui/ModalInc';
import SkeletonWrapper from '../NewProductList/components/SkeletonWrapper/SkeletonWrapper';
import AlertDialog from '../../components/AlertDialog/index.es6';
import { getOrgTier } from '../../lib/utils.es6';
import {
  getOrganizationsRequest,
  createOrganizationRequest,
  resetCreateOrganizationSuccess,
  resetCreateOrganizationError,
  deleteOrganizationRequest,
  resetDeleteOrganizationSuccess,
  resetDeleteOrganizationError,
} from './Redux/actions';

// Deterministic colour for the org-initials avatar from the org code.
var AVATAR_COLORS = [
  '#0B6E4F', '#B91C1C', '#C2410C', '#0F766E',
  '#1E40AF', '#6D28D9', '#9D174D', '#15803D',
];
function avatarColor(code) {
  var s = code || '';
  var sum = 0;
  for (var i = 0; i < s.length; i++) { sum += s.charCodeAt(i); }
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}
function orgInitials(name) {
  if (!name) { return '??'; }
  var parts = name.trim().split(/\s+/).slice(0, 3);
  return parts.map(function(p) { return p.charAt(0).toUpperCase(); }).join('');
}

class OrganizationList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCreateDialog: false,
      name: '',
      code: '',
      showCreateSuccess: false,
      showCreateError: false,
      showDeleteConfirm: false,
      orgToDelete: null,
      showDeleteSuccess: false,
      showDeleteError: false,
      search: '',
      tierFilter: 'all', // 'all' | 'complex' | 'simple'
    };
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
  }

  componentWillMount() {
    this.props.dispatch(getOrganizationsRequest());
  }

  componentDidUpdate(prevProps) {
    var data = this.props.organizationsData;
    var prev = prevProps.organizationsData;
    if (data.created && !prev.created) {
      this.setState({ showCreateDialog: false, showCreateSuccess: true, name: '', code: '' });
      this.props.dispatch(getOrganizationsRequest());
    }
    if (data.createError && !prev.createError) {
      this.setState({ showCreateError: true });
    }
    if (data.deleted && !prev.deleted) {
      this.setState({ showDeleteConfirm: false, orgToDelete: null, showDeleteSuccess: true });
      this.props.dispatch(getOrganizationsRequest());
    }
    if (data.deleteError && !prev.deleteError) {
      this.setState({ showDeleteError: true });
    }
  }

  handleNameChange(e) {
    var name = e.target.value;
    var autoCode = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/, '');
    this.setState({ name: name, code: autoCode });
  }

  handleCreate() {
    var name = this.state.name;
    var code = this.state.code;
    if (!name || !code) { return; }
    this.props.dispatch(createOrganizationRequest({
      guid: uuidv4(), name: name, code: code,
    }));
  }

  handleDeleteConfirm() {
    if (this.state.orgToDelete) {
      this.props.dispatch(deleteOrganizationRequest(this.state.orgToDelete.guid));
    }
  }

  filterOrgs(orgs) {
    var s = this.state.search.toLowerCase().trim();
    var tf = this.state.tierFilter;
    return orgs.filter(function(o) {
      if (s) {
        var haystack = ((o.name || '') + ' ' + (o.code || '')).toLowerCase();
        if (haystack.indexOf(s) === -1) { return false; }
      }
      if (tf !== 'all') {
        var tier = getOrgTier(o).toLowerCase();
        if (tier !== tf) { return false; }
      }
      return true;
    });
  }

  renderTierChip(tier) {
    var isComplex = tier === 'Complex';
    return (
      <Chip
        label={tier}
        size="small"
        sx={{
          height: 22, fontSize: 11, fontWeight: 600,
          bgcolor: isComplex ? '#FEF3C7' : '#DCFCE7',
          color: isComplex ? '#92400E' : '#166534',
          border: '1px solid ' + (isComplex ? '#FDE68A' : '#BBF7D0'),
        }}
      />
    );
  }

  renderFilterChip(label, value, count, active) {
    var self = this;
    return (
      <Chip
        label={label + ' · ' + count}
        clickable
        onClick={function() { self.setState({ tierFilter: value }); }}
        sx={{
          height: 28, fontSize: 12, fontWeight: 600, borderRadius: 1,
          bgcolor: active
            ? (value === 'complex' ? '#FEF3C7' : value === 'simple' ? '#DCFCE7' : '#E0E7FF')
            : '#F9FAFB',
          color: active
            ? (value === 'complex' ? '#92400E' : value === 'simple' ? '#166534' : '#3730A3')
            : '#6B7280',
          border: '1px solid ' + (active
            ? (value === 'complex' ? '#FDE68A' : value === 'simple' ? '#BBF7D0' : '#C7D2FE')
            : '#E5E7EB'),
          '&:hover': { bgcolor: active ? undefined : '#F3F4F6' },
        }}
      />
    );
  }

  render() {
    var data = this.props.organizationsData;
    var state = this.state;
    var orgs = data.list || [];

    if (data.loading) { return <SkeletonWrapper loading={true} />; }

    var complexCount = orgs.filter(function(o) {
      return getOrgTier(o) === 'Complex';
    }).length;
    var simpleCount = orgs.length - complexCount;

    var visible = this.filterOrgs(orgs);

    return (
      <Box sx={{ p: 3, bgcolor: '#FFFFFF', minHeight: '100vh' }}>
        {/* Workspace header */}
        <Typography sx={{
          fontSize: 11, fontWeight: 600, color: '#9CA3AF',
          letterSpacing: 1.2, textTransform: 'uppercase', mb: 0.5,
        }}>
          Workspace
        </Typography>

        <Grid container alignItems="flex-start" justifyContent="space-between"
          spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{
              fontWeight: 700, fontSize: 28, color: '#111827', mb: 0.5,
            }}>
              Organizations
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6B7280', maxWidth: 540 }}>
              One organization holds defaults for many partners.
              Configure surfaces here, override per-partner only when truly needed.
            </Typography>
          </Grid>
          <Grid item>
            <ButtonInc
              variant="contained" color="primary"
              onClick={function() { this.setState({ showCreateDialog: true }); }.bind(this)}>
              + New Organization
            </ButtonInc>
          </Grid>
        </Grid>

        {/* Search + filter chips */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          mb: 2, flexWrap: 'wrap',
        }}>
          <Box sx={{
            flex: 1, minWidth: 240, maxWidth: 400,
            position: 'relative',
          }}>
            <Box component="span" sx={{
              position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 14,
            }}><span role="img" aria-label="search">🔍</span></Box>
            <input
              value={state.search}
              onChange={function(e) { this.setState({ search: e.target.value }); }.bind(this)}
              placeholder="Search by name or code..."
              style={{
                width: '100%', padding: '9px 12px 9px 36px',
                border: '1px solid #E5E7EB', borderRadius: 6,
                fontSize: 13, boxSizing: 'border-box',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {this.renderFilterChip('All', 'all', orgs.length, state.tierFilter === 'all')}
            {this.renderFilterChip('Complex', 'complex', complexCount, state.tierFilter === 'complex')}
            {this.renderFilterChip('Simple', 'simple', simpleCount, state.tierFilter === 'simple')}
          </Box>
        </Box>

        {data.error && (
          <Typography sx={{ color: '#B91C1C', fontSize: 13, mb: 2 }}>
            Failed to load organizations: {String(data.error)}
          </Typography>
        )}

        {/* Table */}
        <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: '#6B7280',
                  textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5 }}>
                  Organization
                </TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: '#6B7280',
                  textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Tier
                </TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: '#6B7280',
                  textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Status
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600,
                  color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6, color: '#9CA3AF' }}>
                    {orgs.length === 0
                      ? 'No organizations yet. Click "+ New Organization" to create one.'
                      : 'No organizations match your filters.'}
                  </TableCell>
                </TableRow>
              )}
              {visible.map(function(o) {
                var tier = getOrgTier(o);
                var isActive = o.active !== false;
                return (
                  <TableRow key={o.guid}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={function() {
                      hashHistory.push('/admin/organizations/' + o.guid);
                    }}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: 1,
                          bgcolor: avatarColor(o.code), color: '#FFFFFF',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                        }}>
                          {orgInitials(o.name)}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                            {o.name}
                          </Typography>
                          <Typography sx={{
                            fontSize: 11, fontFamily: 'monospace', color: '#9CA3AF',
                          }}>
                            {o.code}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{this.renderTierChip(tier)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{
                          width: 7, height: 7, borderRadius: '50%',
                          bgcolor: isActive ? '#10B981' : '#9CA3AF',
                        }} />
                        <Typography sx={{ fontSize: 13, color: '#374151' }}>
                          {isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" onClick={function(e) { e.stopPropagation(); }}>
                      <Tooltip title="View / Edit">
                        <IconButton size="small"
                          onClick={function() {
                            hashHistory.push('/admin/organizations/' + o.guid);
                          }}>
                          <Box component="span" sx={{ fontSize: 16 }}>👁</Box>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small"
                          onClick={function() {
                            this.setState({ showDeleteConfirm: true, orgToDelete: o });
                          }.bind(this)}>
                          <Box component="span" sx={{ fontSize: 16, color: '#B91C1C' }}>🗑</Box>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              }.bind(this))}
            </TableBody>
          </Table>
        </Box>

        {/* Create dialog */}
        <ModalInc
          modalTitle="Create Organization"
          open={state.showCreateDialog}
          close={function() { this.setState({ showCreateDialog: false }); }.bind(this)}
          fullWidth maxWidth="sm"
          modalButtons={[
            <ButtonInc key="cancel" btnType="secondary"
              onClick={function() { this.setState({ showCreateDialog: false }); }.bind(this)}>
              Cancel
            </ButtonInc>,
            <ButtonInc key="create" variant="contained" color="primary"
              loading={data.creating} onClick={this.handleCreate}>
              Create
            </ButtonInc>,
          ]}>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <label style={{ fontWeight: 600 }}>Name *</label>
              <input value={state.name} onChange={this.handleNameChange}
                placeholder="e.g. Acme Insurance"
                style={{ display: 'block', width: '100%', padding: '8px',
                  marginTop: 4, boxSizing: 'border-box' }} />
            </Grid>
            <Grid item xs={12}>
              <label style={{ fontWeight: 600 }}>Code (slug) *</label>
              <input value={state.code}
                onChange={function(e) { this.setState({ code: e.target.value }); }.bind(this)}
                placeholder="e.g. acme_insurance"
                style={{ display: 'block', width: '100%', padding: '8px',
                  marginTop: 4, boxSizing: 'border-box' }} />
              <small style={{ color: '#666' }}>
                Auto-generated from name. Lowercase, underscores only.
              </small>
            </Grid>
          </Grid>
        </ModalInc>

        {state.showCreateSuccess && (
          <AlertDialog show={state.showCreateSuccess} title="Organization Created"
            type="success" showConfirm confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetCreateOrganizationSuccess());
              this.setState({ showCreateSuccess: false });
            }.bind(this)}>
            Organization created successfully.
          </AlertDialog>
        )}
        {state.showCreateError && (
          <AlertDialog show={state.showCreateError} title="Error" type="danger"
            showConfirm confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetCreateOrganizationError());
              this.setState({ showCreateError: false });
            }.bind(this)}>
            {data.createError
              ? (data.createError.message || JSON.stringify(data.createError))
              : 'An error occurred.'}
          </AlertDialog>
        )}
        {state.showDeleteConfirm && (
          <AlertDialog show={state.showDeleteConfirm} title="Delete Organization"
            type="warning" showConfirm showCancel
            confirmBtnText="Yes, Delete" cancelBtnText="Cancel"
            reverseConfirmAndCancelButtons
            onConfirm={this.handleDeleteConfirm}
            onCancel={function() {
              this.setState({ showDeleteConfirm: false, orgToDelete: null });
            }.bind(this)}>
            {state.orgToDelete
              ? 'Are you sure you want to delete "' + state.orgToDelete.name + '"?'
              : ''}
          </AlertDialog>
        )}
        {state.showDeleteSuccess && (
          <AlertDialog show={state.showDeleteSuccess} title="Deleted" type="success"
            showConfirm confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetDeleteOrganizationSuccess());
              this.setState({ showDeleteSuccess: false });
            }.bind(this)}>
            Organization deleted successfully.
          </AlertDialog>
        )}
        {state.showDeleteError && (
          <AlertDialog show={state.showDeleteError} title="Error" type="danger"
            showConfirm confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetDeleteOrganizationError());
              this.setState({ showDeleteError: false });
            }.bind(this)}>
            {data.deleteError
              ? (data.deleteError.message || JSON.stringify(data.deleteError))
              : 'An error occurred.'}
          </AlertDialog>
        )}
      </Box>
    );
  }
}

export default connect(function(state) {
  return { organizationsData: state.organizationsData };
})(OrganizationList);
